import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type {
  MeResponse,
  UpdateMerchantSettingsPayload,
  UpdateMerchantSettingsResponse,
  ValidateGeocodingApiKeyPayload,
  ValidateGeocodingApiKeyResponse,
} from '../types/settings';

export function useMe() {
  const api = useApiClient();
  return useQuery<MeResponse, Error>({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => api.get('/admin/me', signal),
  });
}

/**
 * True only while the very first `/admin/me` fetch (including its retries) is
 * in flight. Gates that hide a subtree must use this, never `isLoading`: a
 * refetch of a query that has only ever failed re-enters `pending` (no data to
 * keep), so `isLoading` turns true again and the subtree unmounts; its own
 * `useMe` observers then refetch on remount (`retryOnMount`), which flips the
 * gate once more - an endless ~2 req/s loop whenever the backend answers 5xx.
 */
export function isInitialMeLoad(
  query: Pick<UseQueryResult<MeResponse, Error>, 'isPending' | 'isFetched'>,
): boolean {
  return query.isPending && !query.isFetched;
}

export function useUpdateMerchantSettings() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<UpdateMerchantSettingsResponse, Error, UpdateMerchantSettingsPayload>({
    mutationFn: (payload) => api.put('/admin/merchant/settings', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useValidateGeocodingApiKey() {
  const api = useApiClient();
  return useMutation<ValidateGeocodingApiKeyResponse, Error, ValidateGeocodingApiKeyPayload>({
    mutationFn: (payload) => api.post('/admin/merchant/settings/validate-geocoding-key', payload),
  });
}
